import React, { useState } from "react";

/**
 * CareLogix™ ORACLE — Platform Report (Phase 16, LOGIX-2™ era)
 * Interactive 11-section sidebar overview. Strictly additive component, mounted
 * as a new top-level mode tab on the public ORACLE app (id: "platform_report").
 *
 * Editorial intent — the report explains the platform, the LOGIX-2 engine, the
 * multi-model intelligence layer, watchtowers, oracle score v2, brokers/sports
 * ecosystems, UI highlights, validation status, roadmap, and legal terms.
 */

const SECTIONS = [
  {
    id: "overview",
    icon: "🔮",
    title: "What Is CareLogix™ ORACLE?",
    paragraphs: [
      "CareLogix™ ORACLE — Multi-Domain Intelligence Command™ — is a production-grade intelligence operating system that turns publicly available information into actionable, executive-grade intelligence across Markets, Capital, Grants, Funding, Contracts, Research, Health, Recovery, Longevity, Governance, and Executive Briefings.",
      "Powered by LOGIX-2™ — the autonomous agentic intelligence engine — the platform continuously Perceives™, Analyzes™, Correlates™, Scores™, Ranks™, Monitors™, Prioritizes™, Alerts™, and Explains™. Every response carries explicit educational disclaimers; the platform makes no profit guarantees.",
    ],
  },
  {
    id: "modes",
    icon: "🎯",
    title: "The 9 ORACLE Modes",
    table: {
      headers: ["Mode", "Focus", "Output Schema"],
      rows: [
        ["📈 Stock Picks", "Equity intelligence · catalysts · institutional edge", "VERDICT · Quick Turnaround · Drivers · Risk · Confidence"],
        ["₿ Crypto Plays", "On-chain · DeFi · L1/L2/AI/RWA flows", "VERDICT · Momentum Signals · On-Chain Edge · Risk"],
        ["🌐 OANDA FX", "G10 FX · metals · energy · macro divergence", "Agent Workflow · FX Verdict · Macro Drivers · Risk Matrix"],
        ["🏆 Sports Bets", "Sharp money · ATS · value vs implied", "ORACLE PICK · Line Analysis · Intel · Stat Edge"],
        ["🎮 Gaming & Esports", "CS2 · LoL · Valorant · Dota 2 · Rocket League", "ESPORTS PICK · Map-Pool Edge · Stat Edge · Live Angle"],
        ["⚡ Options Flow", "Unusual activity · dealer gamma · IV/HV skew", "OPTIONS VERDICT · Unusual Flow · IV Rank · Confidence"],
        ["◈ Swing Trades", "3–21 day technical momentum setups", "SWING VERDICT · Technical Setup · MACD/RSI · Invalidation"],
        ["🔥 Catalyst Plays", "Earnings · FDA · M&A · macro prints", "CATALYST VERDICT · Implied Move · Catalyst Edge"],
        ["🏦 Retirement", "Compound growth · 4% rule · tax wrappers", "RETIREMENT VERDICT · Compound Projection · Optimization"],
      ],
    },
  },
  {
    id: "logix2",
    icon: "🤖",
    title: "LOGIX-2™ — The ORACLE Brain",
    paragraphs: [
      "LOGIX-2™ is the official agentic AI engine of CareLogix™ ORACLE. It applies multi-model intelligence routing across Claude, OpenAI, Gemini, and DeepSeek — selecting the optimal model per asset class, query complexity, and cost band.",
    ],
    cards: [
      { rank: "#1", name: "Claude Sonnet 4.5", provider: "Anthropic", badge: "ORACLE CORE", color: "#e8a020", desc: "Adaptive Reasoning · long-horizon agentic workflows · institutional-grade financial parsing. The gold standard for cross-market mathematical analysis.", best: "Stocks · FX · Finance · Retirement" },
      { rank: "#2", name: "GPT-5 / Codex", provider: "OpenAI", badge: "HIGH FREQ", color: "#60b8e8", desc: "Elite function-calling precision and throughput. Designed for high-frequency odds monitoring and rapid bookmaker line shifts.", best: "Sports · Options · Speed" },
      { rank: "#3", name: "Gemini 2.5", provider: "Google", badge: "MULTIMODAL", color: "#c060e8", desc: "Strong multimodal reasoning across documents, tables, and charts. Useful for catalyst maps and earnings-pack synthesis.", best: "Catalysts · Reports · OCR" },
      { rank: "OS", name: "DeepSeek-V3", provider: "DeepSeek", badge: "ON-CHAIN", color: "#60e8b8", desc: "Cost-efficient deep reasoning at scale. Excellent at tracing smart contracts and on-chain transaction graphs.", best: "Crypto · DeFi · Smart Contracts" },
    ],
    consensusTable: {
      headers: ["Routing Tier", "Models", "Use Case"],
      rows: [
        ["Simple Query", "Lowest-cost model", "Quick reads, factual lookups"],
        ["Research Query", "2-model consensus", "Standard ORACLE reports"],
        ["High-Value Signal", "Consensus + validation", "Live signal fabric outputs"],
        ["Executive Report", "All 4 models", "Executive Dossiers, audit trails"],
      ],
    },
  },
  {
    id: "score",
    icon: "💯",
    title: "Oracle Score™ V2",
    paragraphs: [
      "Every asset, signal, and report carries an Oracle Score™ on a 0–100 scale, computed from 8 weighted intelligence dimensions:",
    ],
    table: {
      headers: ["Dimension", "Weight"],
      rows: [
        ["Momentum", "15%"],
        ["Fundamental", "15%"],
        ["Catalyst", "15%"],
        ["Risk (inverse)", "15%"],
        ["Sentiment", "10%"],
        ["Liquidity", "10%"],
        ["Volatility", "10%"],
        ["Institutional", "10%"],
      ],
    },
    grades: [
      ["95–100", "Elite", "#f5c842"],
      ["90–94", "Exceptional", "#e8a020"],
      ["80–89", "Strong", "#7ac860"],
      ["70–79", "Moderate", "#60b8e8"],
      ["<70", "Monitor", "#7a6a4a"],
    ],
  },
  {
    id: "watchtowers",
    icon: "🗼",
    title: "Watchtowers™",
    paragraphs: [
      "Watchtowers continuously monitor domain-specific intelligence streams and surface high-signal events to the Global Intelligence Chyron™ and the Signal Command™.",
    ],
    bullets: [
      "Markets Watchtower — Stocks · ETFs · Crypto · FX · Options",
      "Healthcare Watchtower — FDA · Biotech · Medical Devices · Clinical Trials",
      "Macro Watchtower — Fed · ECB · BOE · BOJ · CPI · NFP · GDP",
      "Geopolitical Watchtower — Wars · Sanctions · Trade · Elections · Conflict zones",
      "Commodities Watchtower — Gold · Silver · Oil · Natural Gas · Copper · Ag",
      "Sports Watchtower — NFL · NBA · MLB · NHL · NCAA · Soccer · MMA · Boxing",
      "Crypto Watchtower — Whale flows · Exchange inflows · DeFi · L1/L2/AI/RWA",
      "Options Watchtower — Unusual flow · Gamma exposure · Dealer positioning",
    ],
  },
  {
    id: "signals",
    icon: "📡",
    title: "Signal Fabric™ — Signal Types",
    bullets: [
      "Momentum Breakout · Trend Reversal · Volatility Expansion / Compression",
      "Catalyst Event · FDA Catalyst · Earnings Catalyst · M&A Catalyst · Macro Shock · Rate Decision",
      "Insider Buying / Selling · Congressional Buying / Selling",
      "Dark Pool Accumulation / Distribution · Bullish / Bearish Options Flow",
      "Sports Edge · Sharp Money · Line Movement · Injury Edge · Weather Edge · Public Fade",
      "Risk Escalation · Compliance Event",
    ],
  },
  {
    id: "ui",
    icon: "🎨",
    title: "Premium UI Highlights",
    bullets: [
      "Constitutional emerald-and-gold palette throughout (GIOS™ Brand Standard).",
      "CareLogix™ ORACLE Constitutional Logo applied to all surfaces: operator console, public app, login, splash, PDF covers, favicon, PWA icons.",
      "9 mode tabs (horizontally scrollable on mobile) with active-state gold-glow border.",
      "Quick Picks + Sample Queries per mode.",
      "OANDA FX 10-pair grid — click any pair to auto-run an intelligence report.",
      "Global Intelligence Chyron™ — infinite scroll, GPU-accelerated, pause on hover, reduced-motion safe.",
      "Markdown chat bubbles — gold (user) and emerald (assistant) with shield avatar.",
      "PERCEIVE → PLAN → ACT → SYNTHESIZE strip for the FX agentic mode.",
      "Mobile-responsive layout tested at 390×844.",
    ],
  },
  {
    id: "membership",
    icon: "🔐",
    title: "Membership Constitution™",
    paragraphs: [
      "Option B — Soft Gating is constitutional. Restricted endpoints return 402 MEMBERSHIP_REQUIRED. The UI surfaces a gold lock icon, an upgrade modal, and a tier badge.",
    ],
    table: {
      headers: ["Tier", "Inclusions"],
      rows: [
        ["FREE", "Dashboard · Limited Signals · Limited Reports · Limited Watchlists"],
        ["PRO", "Full Signals · Full Chyron · Watchtower Access · PDF Intelligence Dossiers"],
        ["EXECUTIVE", "Signal Fabric™ · Executive Reports · Advanced Watchlists · Priority Intelligence"],
        ["ENTERPRISE", "Operator Console · Multi-user · API Access · Compliance Exports · White-Label"],
      ],
    },
  },
  {
    id: "validation",
    icon: "✅",
    title: "Validation Status",
    statuses: [
      ["Backend regression (Phases 7–14)", "PASS — last green test in /app/test_reports/iteration_14.json (19/19)", true],
      ["Frontend regression (Phases 7–14)", "PASS — /app/test_reports/iteration_15.json (21/21)", true],
      ["Constitutional Brand Activation", "PASS — /app/test_reports/iteration_16.json (27/27)", true],
      ["LOGIX-2 Multi-Model Routing", "ACTIVE — Claude (Anthropic) live via Emergent LLM key", true],
      ["Real notification channels (SMTP / Twilio / Slack / Discord)", "STANDBY — logged but not yet wired", false],
      ["OANDA live data", "REQUIRES API KEY — simulated otherwise", false],
      ["Payments (Stripe / Apple / Google)", "FUTURE READY — Membership tiers mocked", false],
    ],
  },
  {
    id: "roadmap",
    icon: "🚀",
    title: "Roadmap",
    table: {
      headers: ["#", "Feature", "Description"],
      rows: [
        ["1", "Real notification channels", "Wire SMTP, Twilio, Slack, Discord, Push for the Notification Fabric"],
        ["2", "Live OANDA FX data", "Server-side OANDA v20 proxy for live G10 + metals + energy"],
        ["3", "Stripe billing", "Activate live billing for the Membership Constitution™ tiers"],
        ["4", "Live signal feeds", "Polygon.io · Alpha Vantage · The Odds API · SEC EDGAR live ingest expansion"],
        ["5", "Opportunity Radar UI", "Top-N opportunities for today / week / month / quarter / year"],
      ],
    },
  },
  {
    id: "legal",
    icon: "⚖️",
    title: "Legal Disclaimer",
    disclaimer: true,
  },
];

function Table({ headers, rows }) {
  return (
    <div className="platreport__table-wrap">
      <table className="platreport__table">
        <thead>
          <tr>{headers.map((h) => <th key={h}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => <td key={ci}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PlatformReport() {
  const [active, setActive] = useState("overview");
  const sec = SECTIONS.find((s) => s.id === active) || SECTIONS[0];

  return (
    <div className="platreport" data-testid="platform-report">
      <aside className="platreport__nav" aria-label="Platform report sections">
        <div className="platreport__nav-h">Sections</div>
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`platreport__nav-btn ${active === s.id ? "is-active" : ""}`}
            onClick={() => setActive(s.id)}
            data-testid={`platreport-nav-${s.id}`}
          >
            <span aria-hidden="true">{s.icon}</span>
            <span>{s.title}</span>
          </button>
        ))}
        <div className="platreport__badges">
          {[
            ["LOGIX-2™", "#e8a020"],
            ["GIOS™", "#f5c842"],
            ["PRODUCTION", "#7ac860"],
            ["MOBILE ✓", "#60b8e8"],
          ].map(([t, c]) => (
            <span key={t} className="platreport__badge" style={{ borderColor: c, color: c }}>{t}</span>
          ))}
        </div>
      </aside>

      <section className="platreport__body" data-testid={`platreport-body-${sec.id}`}>
        <header className="platreport__hd">
          <span className="platreport__hd-icon" aria-hidden="true">{sec.icon}</span>
          <div>
            <h2 className="platreport__hd-title">{sec.title}</h2>
            <div className="platreport__hd-sub">CareLogix™ ORACLE · LOGIX-2™ Platform Report</div>
          </div>
        </header>

        <div className="platreport__content">
          {(sec.paragraphs || []).map((p, i) => <p key={i} className="platreport__p">{p}</p>)}

          {sec.table && <Table headers={sec.table.headers} rows={sec.table.rows} />}

          {sec.cards && (
            <div className="platreport__cards">
              {sec.cards.map((c, i) => (
                <article key={i} className="platreport__card">
                  <div className="platreport__card-lt">
                    <span className="platreport__card-rank" style={{ color: c.color }}>{c.rank}</span>
                    <span className="platreport__card-badge" style={{ color: c.color, borderColor: c.color }}>{c.badge}</span>
                  </div>
                  <div className="platreport__card-body">
                    <div className="platreport__card-row">
                      <span className="platreport__card-name" style={{ color: c.color }}>{c.name}</span>
                      <span className="platreport__card-provider">{c.provider}</span>
                      <span className="platreport__card-best">{c.best}</span>
                    </div>
                    <p className="platreport__card-desc">{c.desc}</p>
                  </div>
                </article>
              ))}
            </div>
          )}

          {sec.consensusTable && (
            <>
              <h4 className="platreport__h4">Multi-Model Routing</h4>
              <Table headers={sec.consensusTable.headers} rows={sec.consensusTable.rows} />
            </>
          )}

          {sec.grades && (
            <div className="platreport__grades">
              {sec.grades.map(([range, label, color]) => (
                <div key={range} className="platreport__grade" style={{ borderColor: color }}>
                  <span className="platreport__grade-range" style={{ color }}>{range}</span>
                  <span className="platreport__grade-label">{label}</span>
                </div>
              ))}
            </div>
          )}

          {sec.bullets && (
            <ul className="platreport__list">
              {sec.bullets.map((b, i) => <li key={i}><span aria-hidden="true">◆</span> {b}</li>)}
            </ul>
          )}

          {sec.statuses && (
            <div className="platreport__statuses">
              {sec.statuses.map(([label, detail, ok], i) => (
                <div key={i} className={`platreport__status ${ok ? "is-ok" : "is-warn"}`}>
                  <span className="platreport__status-dot" />
                  <div>
                    <div className="platreport__status-label">{label}</div>
                    <div className="platreport__status-detail">{detail}</div>
                  </div>
                  <span className={`platreport__status-tag ${ok ? "is-ok" : "is-warn"}`}>{ok ? "✅ PASS" : "⏳ STANDBY"}</span>
                </div>
              ))}
            </div>
          )}

          {sec.disclaimer && (
            <div className="platreport__legal">
              <div className="platreport__legal-h">⚠️ Important — Please Read</div>
              <p>CareLogix™ ORACLE is a research and educational platform only. It does not constitute financial, investment, trading, medical, or legal advice.</p>
              <p>All investments carry risk of loss. Past performance is not indicative of future results. Forex trading involves substantial risk and is not appropriate for all investors.</p>
              <p>Sports and esports betting are for entertainment purposes only, must be conducted legally, and are restricted to persons aged 21+ in applicable jurisdictions.</p>
              <p>CareLogix™ ORACLE does not execute trades, place bets, or manage funds. High-impact decisions require independent human judgment.</p>
              <p>OANDA FX reports are generated from publicly available data. Live API integration requires a secure server-side proxy subject to OANDA&apos;s terms of service.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
